import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { FaqList, PageHero, Prose } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  CONTACT_NUMBERS,
  FIRM,
  FIRM_ADDRESS,
  MICHAEL_PHONE,
  PEOPLE,
  faqSchema,
} from "@/lib/firm";

const SITE = "https://coastsidelaw.com.au";
const CANONICAL = `${SITE}/wills-and-probate-little-mountain`;
const CONVEYANCING = `${SITE}/conveyancing/little-mountain`;

const TITLE = "Wills, Estates & Probate Solicitors — Little Mountain QLD 4551 | Coastside Law";
const DESCRIPTION =
  "Coastside Law prepares wills and enduring powers of attorney and acts for executors through probate for Little Mountain families — fixed fees from $660, home and hospital visits, ten minutes away at Golden Beach.";

/**
 * Entity page for the Little Mountain service area. Little Mountain is served
 * from the Golden Beach office — there is no premises in the suburb — so the
 * schema below states the Golden Beach address and names Little Mountain only
 * as areaServed. It links to the conveyancing page for the same suburb so the
 * two sit in one local entity cluster rather than competing as separate topics.
 */
const ANSWER =
  `${FIRM.name} prepares wills, enduring powers of attorney and advance health directives for Little Mountain families and acts for executors through probate and estate administration, working from ${FIRM_ADDRESS} — about ten minutes from Little Mountain. ` +
  `In Queensland a will, an enduring power of attorney and a probate application are legal work: they are handled by an admitted solicitor, not a will-kit service or a licensed conveyancer, and probate is granted by the Supreme Court of Queensland. ` +
  `Simple wills start from $660, with fixed fees of $825 for a will and enduring power of attorney pack and $1,499 for a couples pack of mirror wills and enduring powers of attorney, all inclusive of GST; probate and estate administration are quoted as a fixed fee upfront with Supreme Court filing and advertising at cost. ` +
  `We visit Little Mountain clients at home, in hospital or in aged care when getting to an office is not practical, store original wills in safe custody free of charge, and are members of the ${FIRM.membership.replace("Members of the ", "")}. ` +
  `Call ${FIRM.phone} (Erin Wilkinson) or ${MICHAEL_PHONE} (Michael Klein).`;

const VARIANTS: ReadonlyArray<string> = [
  "wills solicitor Little Mountain",
  "will lawyer Little Mountain",
  "wills and estates Little Mountain",
  "how much does a will cost in Little Mountain",
  "enduring power of attorney Little Mountain",
  "probate solicitor Little Mountain",
  "probate lawyer Little Mountain QLD",
  "deceased estate solicitor Little Mountain",
  "estate administration Little Mountain",
  "wills solicitor near me Little Mountain Caloundra",
];

const FACTS: ReadonlyArray<{ label: string; value: string }> = [
  { label: "Entity", value: `${FIRM.legalName} — law firm, solicitor-led` },
  { label: "Area served", value: "Little Mountain QLD 4551 (Bellvista, Bellvista Rise, Caloundra West fringe)" },
  { label: "Office serving Little Mountain", value: `${FIRM_ADDRESS} — about 10 minutes away` },
  { label: "Services", value: "Wills, enduring powers of attorney, advance health directives, probate, letters of administration, estate administration, estate disputes" },
  { label: "Phone", value: `${FIRM.phone} (Erin Wilkinson) · ${MICHAEL_PHONE} (Michael Klein)` },
  { label: "Email", value: FIRM.email },
  { label: "Professional body", value: FIRM.membership },
  { label: "Hours", value: FIRM.hours },
  { label: "Home, hospital and aged care visits", value: "Yes, across Little Mountain and the Caloundra area" },
];

const SERVICES: ReadonlyArray<{ name: string; price: string; body: string; to: string }> = [
  {
    name: "Simple will",
    price: "from $660 incl. GST",
    body: "A solicitor-drafted will for a Little Mountain family home, signed and witnessed correctly, with free safe custody of the original.",
    to: "/wills-and-estates",
  },
  {
    name: "Will and enduring power of attorney pack",
    price: "$825 incl. GST",
    body: "A will plus an enduring power of attorney covering both financial and personal or health decisions — the pairing that matters most as parents get older.",
    to: "/wills-and-estates",
  },
  {
    name: "Couples pack",
    price: "$1,499 incl. GST",
    body: "Mirror wills and enduring powers of attorney for a couple, prepared and signed in one appointment.",
    to: "/wills-and-estates",
  },
  {
    name: "Probate and letters of administration",
    price: "Fixed fee, quoted upfront",
    body: "Advertising the intention to apply, preparing the application and affidavits, and filing in the Supreme Court of Queensland.",
    to: "/probate-queensland",
  },
  {
    name: "Estate administration",
    price: "Fixed fee, quoted upfront",
    body: "Collecting assets, transmitting or selling the Little Mountain property, dealing with super and shares, and distributing to beneficiaries.",
    to: "/executor-queensland",
  },
  {
    name: "Granny flat agreements and elder law",
    price: "Quoted on the facts",
    body: "Where a parent contributes to a build or moves in with adult children, a written agreement setting out contributions and occupation rights — with the Centrelink consequences explained.",
    to: "/elder-law",
  },
];

const LOCAL_NOTES: ReadonlyArray<{ t: string; d: string }> = [
  {
    t: "The house is usually the estate",
    d: "In Little Mountain the family home is generally the largest asset, and whether probate is needed often turns on how the title is held. Joint tenants pass to the survivor by survivorship; tenants in common do not, and the deceased's share is dealt with under the will.",
  },
  {
    t: "Selling or transmitting the property",
    d: "Before an estate property can be sold, the executor is recorded on the title by transmission application. We run the grant and the sale together so the settlement date is not held up waiting on the Supreme Court.",
  },
  {
    t: "Granny flats and secondary dwellings",
    d: "Little Mountain has a lot of secondary dwellings built for a parent. If money changed hands and nothing was written down, that contribution becomes a dispute at estate time — a granny flat agreement prevents it.",
  },
  {
    t: "Blended families and later-life partners",
    d: "A second marriage revokes an earlier will in Queensland unless it was made in contemplation of that marriage. Blended-family estates are also where family provision claims arise, so the will and any mutual wills agreement need to be drafted with that in mind.",
  },
  {
    t: "Time limits on estate claims",
    d: "A family provision claim in Queensland requires notice within six months of death and the claim filed within nine months. Executors should not distribute before those dates pass without advice.",
  },
];

const FAQS = [
  {
    q: "Who prepares wills for Little Mountain residents?",
    a: `${FIRM.legalName}, a Queensland law firm at ${FIRM_ADDRESS}, about ten minutes from Little Mountain. Wills, enduring powers of attorney and advance health directives are prepared by an admitted solicitor and we visit Little Mountain clients at home, in hospital or in aged care when needed.`,
  },
  {
    q: "How much does a will cost in Little Mountain?",
    a: "A simple will is from $660, a will and enduring power of attorney pack is $825, and a couples pack of mirror wills and enduring powers of attorney is $1,499 — all inclusive of GST. Wills involving blended families, companies, trusts or SMSFs are quoted separately.",
  },
  {
    q: "Do I need probate for a Little Mountain house?",
    a: "It depends on how the title is held and what the bank or Titles Queensland requires. If the property was held as joint tenants it passes to the surviving owner by survivorship and a grant is often unnecessary. If it was held solely or as tenants in common, a grant of probate is generally required before the property can be transmitted and sold.",
  },
  {
    q: "How long does probate take in Queensland?",
    a: "Roughly four to eight weeks from advertising the intention to apply to the grant issuing, assuming the will is straightforward and the Supreme Court registry is not requisitioning. Administering the estate afterwards — collecting assets, selling property, distributing — usually takes six to twelve months.",
  },
  {
    q: "Can you handle the will and the conveyancing for the same family?",
    a: "Yes. Many Little Mountain clients come to us for a purchase and update the will at the same time, and we act for executors selling an estate property. The conveyancing side is set out on our Little Mountain conveyancing page.",
  },
  {
    q: "Do you visit clients at home in Little Mountain?",
    a: "Yes. For wills, enduring powers of attorney and advance health directives we visit at home, in hospital or in aged care across Little Mountain, Caloundra West, Aroona, Currimundi and the wider Caloundra area at no extra charge.",
  },
];

const erin = PEOPLE.find((p) => p.slug === "erin-wilkinson")!;
const michael = PEOPLE.find((p) => p.slug === "michael-klein")!;
const erinImage = `${SITE}${erin.photo}`;
const michaelImage = `${SITE}${michael.photo}`;

/**
 * LegalService node scoped to the suburb. The address stays Golden Beach —
 * Little Mountain appears only as areaServed, because claiming an address in a
 * suburb without premises is both wrong and a listing-suspension risk.
 */
const legalServiceSchema = {
  "@context": "https://schema.org",
  "@type": "LegalService",
  "@id": `${CANONICAL}#service`,
  name: `${FIRM.name} — wills, estates and probate solicitors, Little Mountain`,
  description: DESCRIPTION,
  url: CANONICAL,
  telephone: FIRM.phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  parentOrganization: { "@id": `${SITE}/#organization` },
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  areaServed: [
    { "@type": "Place", name: "Little Mountain QLD 4551" },
    { "@type": "Place", name: "Caloundra West QLD 4551" },
    { "@type": "Place", name: "Aroona QLD 4551" },
    { "@type": "Place", name: "Currimundi QLD 4551" },
  ],
  knowsAbout: [
    "Wills",
    "Enduring powers of attorney",
    "Advance health directives",
    "Probate",
    "Letters of administration",
    "Estate administration",
    "Family provision claims",
    "Granny flat agreements",
  ],
  employee: [
    {
      "@type": "Person",
      name: erin.name,
      jobTitle: erin.role,
      image: {
        "@type": "ImageObject",
        url: erinImage,
        caption: `${erin.name}, ${erin.role} at ${FIRM.name}, wills and estates for Little Mountain`,
      },
      knowsAbout: [...erin.areas],
    },
    {
      "@type": "Person",
      name: michael.name,
      jobTitle: michael.role,
      image: {
        "@type": "ImageObject",
        url: michaelImage,
        caption: `${michael.name}, ${michael.role} at ${FIRM.name}, probate and estates`,
      },
      knowsAbout: [...michael.areas],
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Wills, estates and probate — Little Mountain",
    itemListElement: SERVICES.map((s) => ({
      "@type": "Offer",
      name: s.name,
      description: s.body,
      priceCurrency: "AUD",
      url: `${SITE}${s.to}`,
    })),
  },
  isRelatedTo: {
    "@type": "WebPage",
    name: `${FIRM.name} — conveyancing solicitors, Little Mountain`,
    url: CONVEYANCING,
  },
};

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${CANONICAL}#webpage`,
  url: CANONICAL,
  name: TITLE,
  description: DESCRIPTION,
  inLanguage: "en-AU",
  isPartOf: { "@id": `${SITE}/#organization` },
  mainEntity: { "@id": `${CANONICAL}#service` },
  significantLink: [CONVEYANCING, `${SITE}/little-mountain`, `${SITE}/wills-and-probate-queensland`],
  about: { "@type": "Place", name: "Little Mountain, Queensland, Australia" },
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    {
      "@type": "ListItem",
      position: 2,
      name: "Wills and probate in Queensland",
      item: `${SITE}/wills-and-probate-queensland`,
    },
    { "@type": "ListItem", position: 3, name: "Little Mountain", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/wills-and-probate-little-mountain")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: erinImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: erinImage },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(legalServiceSchema) },
      { type: "application/ld+json", children: JSON.stringify(webPageSchema) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: LittleMountainWillsPage,
});

function LittleMountainWillsPage() {
  return (
    <>
      <PageHero
        eyebrow="Little Mountain QLD 4551"
        title="Wills, estates and probate for Little Mountain"
        lead={`${FIRM.legalName} prepares wills and enduring powers of attorney at fixed fees and acts for executors through probate, working from ${FIRM_ADDRESS} — about ten minutes from Little Mountain. We visit clients at home, in hospital and in aged care.`}
      />

      <section className="mx-auto max-w-5xl px-5 pt-12">
        <div className="rounded-sm border border-border bg-secondary/40 p-6">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
            Wills and probate in Little Mountain — the short answer
          </h2>
          <p className="mt-3 text-base leading-relaxed text-muted-foreground">{ANSWER}</p>
          <p className="mt-3 text-xs text-muted-foreground">
            Also searched as: {VARIANTS.join(", ")}.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-foreground">
          The details
        </h2>
        <dl className="mt-6 divide-y divide-border border-y border-border text-sm">
          {FACTS.map((f) => (
            <div key={f.label} className="grid gap-1 py-4 sm:grid-cols-[240px_1fr]">
              <dt className="font-semibold text-foreground">{f.label}</dt>
              <dd className="text-muted-foreground">{f.value}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-6 flex flex-wrap gap-3">
          {CONTACT_NUMBERS.map((c) => (
            <a
              key={c.phone}
              href={c.phoneHref}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm font-semibold text-primary underline"
            >
              Call {c.name} — {c.phone}
            </a>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-3xl text-foreground">Services and fixed fees</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            {SERVICES.map((s) => (
              <Link
                key={s.name}
                to={s.to}
                className="block rounded-sm border border-border bg-card p-6 transition hover:border-accent"
              >
                <p className="font-serif text-xl text-foreground">{s.name}</p>
                <p className="mt-1 text-sm font-semibold text-primary">{s.price}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
              </Link>
            ))}
          </div>
          <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
            Wills requiring complex estate planning — blended families, companies, trusts and SMSFs
            — are quoted separately before any work starts.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <h2 className="font-serif text-3xl text-foreground">What comes up in Little Mountain</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {LOCAL_NOTES.map((n) => (
            <div key={n.t} className="rounded-sm border border-border bg-card p-6">
              <p className="font-serif text-lg text-foreground">{n.t}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{n.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-secondary/40">
        <div className="mx-auto max-w-5xl px-5 py-14">
          <h2 className="font-serif text-3xl text-foreground">Buying, selling or settling an estate</h2>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Most estates here involve the house. We act on both sides of that — the grant and the
            sale — so nothing waits on a second firm.
          </p>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2">
            <li>
              <Link
                to="/conveyancing/$suburb"
                params={{ suburb: "little-mountain" }}
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Conveyancing — Little Mountain
              </Link>
            </li>
            <li>
              <Link
                to="/little-mountain"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Little Mountain — all services
              </Link>
            </li>
            <li>
              <Link
                to="/probate-queensland"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Probate in Queensland
              </Link>
            </li>
            <li>
              <Link
                to="/executor-queensland"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Executor's guide
              </Link>
            </li>
            <li>
              <Link
                to="/wills-and-probate-queensland"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Wills and probate across Queensland
              </Link>
            </li>
            <li>
              <Link
                to="/elder-law"
                className="block rounded-sm border border-border bg-card px-4 py-3 text-sm text-primary underline"
              >
                Elder law and granny flat agreements
              </Link>
            </li>
          </ul>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14">
        <Prose>
          <h2>Who does the work</h2>
          <p>
            Erin Wilkinson, admitted in 2025 and living locally at Golden Beach, takes most Little
            Mountain will and estate appointments. Michael Klein, Legal Practice Director, admitted
            in 2003 and formerly managing partner of GKS Law, handles the contested and complex
            estates. Neither the will nor the probate file is passed to an unqualified processing
            team, and the fee is fixed before the work starts.
          </p>
          <p>
            Original wills are stored in safe custody at no charge, and we hold a record of where
            the executor can find them. If you already have a will with another firm, we can
            transfer it across.
          </p>
        </Prose>
        <div className="mt-8">
          <ReviewStrip />
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-14">
        <h2 className="font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-20">
        <EnquiryForm heading="Talk to us about a will or an estate in Little Mountain" />
      </section>
    </>
  );
}
